import React, { useState, useEffect, useRef } from 'react';
import DraggableContainer from './draggablecontainer';
import { useProfileStore } from '../../../state/profileStore';
import styled from 'styled-components';
import ExplainCustomerFocusStep from './explaincustomerfocusstep';
import ServiceDistanceStep from './servicedistancestep'; // Use lowercase path
import UserFocusStep from './userfocusstep';
import {
  saveStep,
  clearProfileAssistantState,
  persistStepToDatabase
} from '../../../utils/profileAssistantState';

// Allow Unicode letters from any script (Thai/Japanese/Cyrillic/Arabic/etc.),
// combining marks (Thai vowels, Hindi matras, Arabic diacritics), whitespace,
// apostrophes, and hyphens. The old `[^a-zA-Z\s'-]` regex stripped every
// non-ASCII letter, turning a Thai name into an empty string — which the
// backend silently accepted, leaving the user with blank u_first_name /
// u_last_name and breaking downstream UI (no display name, no welcome toast,
// etc.). \p{L} = letter, \p{M} = combining mark, /u flag enables Unicode
// property escapes. Still strips digits and most punctuation, which have no
// place in a real person's name.
const sanitizeInput = (input) => {
  const trimmed = input.trim();
  return trimmed.replace(/[^\p{L}\p{M}\s'\-]/gu, '');
};


const Content = styled.div`
  padding: 0px 24px 24px 24px; /* top right bottom left */
  width: 350px;
  height: auto;
  background: #ffffff;
  border-radius: 8px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  margin-top: 0px;
`;

const InputField = styled.input`
  width: 75%;
  padding: 6px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;
  margin-left: 0;
  marginTop: '4px'

  &:focus {
    border-color: #00aef7;
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 174, 247, 0.1);
  }
`;

const Button = styled.button`
  margin-top: 16px;
  padding: 4px 24px;
  background: #00aef7;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: #0098d8;
  }
`;

const ProfileAssistant = ({ onClose, isNewUser }) => {
  const { profile, updateProfile } = useProfileStore();
  // Determine starting step only for new users
  const defaultStep = profile?.u_is_new_user && !profile?.u_onboarding_stage
    ? 'collectName'
    : profile?.u_onboarding_stage === 'ongoing' && isNewUser
    ? 'ongoing'
    : profile?.u_onboarding_stage === 'completed' 
    ? 'closed'
    : 'closed'; // Default to closed for existing users or completed
  const [step, setStep] = useState(defaultStep);
    const isMounted = useRef(false); // Track initial mount
    console.log('🧠 u_onboarding_stage value from profile:', profile?.u_onboarding_stage);
    console.log('🧩 step set in useState:', step);
    console.log('🧠 ProfileStore profile object:', profile);

    const containerRef = useRef(null);

  const handleClose = () => {
    if (step === 'ongoing') {
      const _tok = localStorage.getItem('token');
      fetch(`/api/users/update-onboarding-stage`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(_tok && { Authorization: `Bearer ${_tok}` }),
        },
        credentials: 'include',
        body: JSON.stringify({ u_onboarding_stage: 'completed' }),
      });
      updateProfile({ u_onboarding_stage: 'completed' });
      setStep('closed');
    }
    onClose();
  };

    useEffect(() => {
      if (containerRef.current) {
        containerRef.current.style.opacity = '1';
      }
      // Set isMounted after first render
      isMounted.current = true;
    }, []);

  const handleNameSubmission = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      alert('Please enter both first and last names.');
      return;
    }

    try {
      const cleanedFirst = sanitizeInput(firstName);
      const cleanedLast = sanitizeInput(lastName);

      const response = await fetch(`/api/new-user/update-profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          u_first_name: cleanedFirst,
          u_last_name: cleanedLast,
          u_id: profile?.u_id,
          u_role: 'owner' // Force first user as owner
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update user: ${errorText}`);
      }

      await response.json();

      // Set u_url_name
      try {
        const base = `${cleanedFirst.replace(/\s+/g, '_')}_${cleanedLast.replace(/\s+/g, '_')}`;
        for (let i = 1; i <= 20; i++) {
          const candidate = i === 1 ? base : `${base}_${i}`;
          const r = await fetch(`/api/new-user/update-profile`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              userId: profile?.u_id,
              userData: { u_url_name: candidate },
              businessData: {}
            })
          });

          if (r.ok) break;
          const txt = await r.text();
          const isDup = r.status === 409 || /duplicate key|unique/i.test(txt);
          if (!isDup) break;
        }
      } catch (e) {
        console.error('u_url_name set failed:', e);
      }

      // ── LEAN ONBOARDING (2026-05-26) ──────────────────────────────────────
      // Name is the ONLY question this wizard asks now. The old flow then
      // walked through explainCompanyFocus -> askUserFocus -> askServiceDistance,
      // which is exactly what real signups (e.g. Springfield Plumbing u_id=857)
      // bailed on. Those 3 questions moved to the profile editor's new
      // "Update Focus and Logistics" popup (Slice 2 of this work) + a blocking
      // modal on first Teams-page access (Slice 4). They're irrelevant to the
      // Design Center and Sharing Center, which is where new users actually
      // want to go after signup.
      //
      // Mark onboarding COMPLETE in the DB (sets u_is_new_user=false AND
      // u_onboarding_stage=NULL — the existing /complete-onboarding endpoint
      // already does both atomically). That stops the wizard from re-firing
      // on next login. Then mirror into Zustand so the docking station's
      // ternary doesn't try to re-open this assistant on the same session.
      try {
        const _tok = localStorage.getItem('token');
        await fetch(`/api/users/complete-onboarding`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(_tok && { Authorization: `Bearer ${_tok}` }),
          },
          credentials: 'include',
        });
      } catch (markErr) {
        // Non-blocking — user can still proceed; worst case the wizard
        // re-fires on next login and they fill the name again. Better than
        // dead-ending on a transient network blip.
        console.warn('[lean-onboarding] failed to mark complete (non-blocking):', markErr);
      }

      updateProfile({
        u_first_name: cleanedFirst,
        u_last_name: cleanedLast,
        u_role: 'owner',
        u_is_new_user: false,
        u_onboarding_stage: null,
      });

      // Clean up any residual wizard state from localStorage
      clearProfileAssistantState();

      // Set a one-shot flag so DockingStation knows to show the welcome
      // toast pointing at the assistant icon. Value stores the user's first
      // name so the toast can greet them by name. Cleared after first display
      // by DockingStation, so it never re-fires.
      //
      // BOTH a localStorage write AND a custom event dispatch — the event is
      // what makes the toast fire RIGHT NOW (DockingStation is already mounted
      // by the time this runs, so its mount-effect that polls localStorage
      // already ran and missed the flag). localStorage is the fallback path
      // for the case where the page is refreshed before the toast can fire
      // (e.g. user closes wizard then immediately reloads).
      try {
        if (profile?.u_id) {
          localStorage.setItem(`eh_show_welcome_toast_u${profile.u_id}`, cleanedFirst);
          window.dispatchEvent(new CustomEvent('eh:show-welcome-toast', {
            detail: { u_id: profile.u_id, name: cleanedFirst },
          }));
        }
      } catch (e) { /* localStorage disabled — toast just won't show, no error */ }

      // Close the assistant. User lands on their profile page with NO
      // assistant open — they can click the docking-station icon any time
      // to open the OngoingProfileAssistant for further edits.
      setStepAndStore('closed');
      onClose();
    } catch (error) {
      console.error('Error saving names:', error);
      alert('Failed to save names. Please try again.');
    }
  };
  const [, setTypedText] = useState('');
  const [firstName, setFirstName] = useState(profile?.u_first_name || '');
  const [lastName, setLastName] = useState(profile?.u_last_name || '');
  const setStepAndStore = (nextStep) => {
  setStep(nextStep);
  saveStep(nextStep);
};
  //const setStep = (newStep) => {
  //setStep(newStep);
  //localStorage.setItem('profileAssistantStep', newStep);
  //};
  //useEffect(() => {
  //const savedFirst = localStorage.getItem('profileAssistant_firstName');
  //const savedLast = localStorage.getItem('profileAssistant_lastName');
  //console.log('Loaded from localStorage:', { savedFirst, savedLast });
  //if (savedFirst) setFirstName(savedFirst);
  //if (savedLast) setLastName(savedLast);
  //}, []);

  useEffect(() => {
    console.log('Step set successfully. UI should now change.');
    }, [step]);

  const welcomeMessage = "Hey there!! Welcome to eHandshake. My name is Jax. I'm your profile assistant.\n\nCan I get your first and last name, please?";
  
  useEffect(() => {
    console.log('🧭 Skipping legacy logic. Using u_onboarding_stage from DB:', profile?.u_onboarding_stage);
  }, [profile]);




/*
useEffect(() => {
  const savedStep = localStorage.getItem('profileAssistantStep');
  if (savedStep) {
    console.log('Resuming saved step:', savedStep);
    setStep(savedStep);
    return;
  }

  // Fallback logic if no saved step
  const defaultFirst = 'First Name';
  const defaultLast = 'Last Name';

  if (profile?.u_first_name === defaultFirst || profile?.u_last_name === defaultLast) {
    setStepAndStore('collectName');
  } else if (profile?.u_is_new_user) {
    setStepAndStore('explainCompanyFocus');
  }
}, [profile]);
*/

  useEffect(() => {
    if (step === 'collectName') {
      setTypedText('');
      let currentChar = 0;
      const typeSpeed = 40;

      const typeText = () => {
        if (currentChar < welcomeMessage.length) {
          setTypedText(welcomeMessage.slice(0, currentChar + 1));
          currentChar++;
          setTimeout(typeText, typeSpeed);
        }
      };

      setTimeout(typeText, 1000);
    }
  }, [step]);



  const switchStepRender = () => {
    if (step === 'closed') return null; // Prevent render if closed
    switch (step) {
      case 'collectName':
        return (
          <>
            <div style={{ marginTop: '10px' }}>
              <p style={{ whiteSpace: 'pre-line' }}>{welcomeMessage}</p>
            </div>
            <div
              style={{
                marginTop: '10px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: '100%',
              }}
            >
              <InputField
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                style={{ width: '85%', marginBottom: '12px' }}
              />
              <InputField
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                style={{ width: '85%', marginBottom: '12px' }}
              />
            </div>
            <Button onClick={handleNameSubmission}>Nice to meet you!</Button>
          </>
        );

      case 'explainCompanyFocus':
        return (
          <ExplainCustomerFocusStep
            profile={profile}
            updateProfile={updateProfile}
            stepSetter={setStepAndStore}
          />
        );

      case 'askUserFocus':
        return (
          <UserFocusStep
            profile={profile}
            updateProfile={updateProfile}
            stepSetter={setStepAndStore}
          />
        );

      case 'askServiceDistance':
        return (
          <ServiceDistanceStep
            profile={profile}
            updateProfile={updateProfile}
            goToNextStep={async () => {
              clearProfileAssistantState();
              // ✅ Step 1: Persist final step to DB
              const _tok2 = localStorage.getItem('token');
              await fetch(`/api/users/update-onboarding-stage`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  ...(_tok2 && { Authorization: `Bearer ${_tok2}` }),
                },
                credentials: 'include',
                body: JSON.stringify({
                  u_onboarding_stage: 'ongoing',
                }),
              });
              // ✅ Step 2: Update Zustand store — marks onboarding complete
              updateProfile({ u_onboarding_stage: 'ongoing', u_is_new_user: false });
              // ✅ Step 3: Do NOT call handleClose() here.
              // DockingStation's ternary will automatically switch from
              // ProfileAssistant to OngoingProfileAssistant because
              // u_is_new_user is now false while activeAssistant stays 'profile'.
            }}

          />
        );

      default:
        return <p>Let's complete your profile...</p>;
    }
  };


      
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0)',
        zIndex: 1000,
      }}
    >
      <DraggableContainer
        ref={containerRef}
        onClose={handleClose}
        title="Profile Assistant"
      >

        <Content
          style={{
            maxHeight: '400px',
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '0px 24px 24px 24px',
            backgroundColor: '#ffffff',
          }}
        >
          <div style={{ marginTop: '5px' }}>
            {switchStepRender()}
          </div>
        </Content>
      </DraggableContainer>
    </div>
  );
};

export default ProfileAssistant;